import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { clientGroupAPI, workspaceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  FolderOpen, Plus, Building2, MapPin, Trash2, Edit2, Globe, FileText,
  BarChart3, TrendingUp, ChevronDown, ChevronUp, X, Users, UserPlus,
  CalendarDays, History
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';

const PLATFORMS = ['facebook', 'instagram', 'linkedin', 'youtube', 'pinterest', 'gmb', 'substack'];

const PLATFORM_COLORS = {
  facebook: 'bg-[#1877F2]/15 text-[#1877F2] border-[#1877F2]/20',
  instagram: 'bg-[#E1306C]/15 text-[#E1306C] border-[#E1306C]/20',
  linkedin: 'bg-[#0A66C2]/15 text-[#0A66C2] border-[#0A66C2]/20',
  youtube: 'bg-[#FF0000]/15 text-[#FF0000] border-[#FF0000]/20',
  pinterest: 'bg-[#E60023]/15 text-[#E60023] border-[#E60023]/20',
  gmb: 'bg-[#1A73E8]/15 text-[#1A73E8] border-[#1A73E8]/20',
  substack: 'bg-[#FF6719]/15 text-[#FF6719] border-[#FF6719]/20',
};

const CHART_COLORS = [
  'hsl(174, 74%, 34%)', 'hsl(199, 84%, 48%)', 'hsl(40, 92%, 56%)',
  'hsl(142, 72%, 44%)', 'hsl(0, 72%, 52%)', '#E1306C', '#0A66C2'
];

const STATUS_COLORS = {
  'Published': 'hsl(174, 74%, 34%)',
  'Scheduled': 'hsl(199, 84%, 48%)',
  'Approved': 'hsl(142, 72%, 44%)',
  'In Review': 'hsl(40, 92%, 56%)',
  'Draft': 'hsl(215, 20%, 55%)',
  'Rejected': 'hsl(0, 72%, 52%)',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover/90 backdrop-blur border rounded-lg p-3 text-xs shadow-lg">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color || p.fill }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Group Analytics Panel ───
function GroupAnalyticsPanel({ workspaceId, group }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId || !group?.id) return;
    setLoading(true);
    clientGroupAPI.analytics(workspaceId, group.id)
      .then(res => setAnalytics(res.data))
      .catch(() => toast.error('Failed to load group analytics'))
      .finally(() => setLoading(false));
  }, [workspaceId, group?.id]);

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-secondary rounded-lg" />)}
        </div>
        <div className="h-48 bg-secondary rounded-lg" />
      </div>
    );
  }

  if (!analytics) return null;

  const summary = analytics.summary || {};
  const hasData = analytics.total_posts > 0;

  return (
    <div className="mt-4 space-y-4" data-testid={`group-analytics-${group.id}`}>
      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: 'Total Posts', value: analytics.total_posts, color: 'text-foreground' },
          { label: 'Published', value: summary.published || 0, color: 'text-emerald-400' },
          { label: 'Scheduled', value: summary.scheduled || 0, color: 'text-cyan-400' },
          { label: 'Approved', value: summary.approved || 0, color: 'text-green-400' },
          { label: 'In Review', value: summary.in_review || 0, color: 'text-amber-400' },
          { label: 'Draft', value: summary.draft || 0, color: 'text-muted-foreground' },
        ].map((kpi, i) => (
          <div key={i} className="rounded-lg bg-secondary/40 p-2.5 text-center">
            <p className={`text-lg font-display font-semibold ${kpi.color}`} data-font="display">{kpi.value}</p>
            <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
          </div>
        ))}
      </div>

      {hasData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Status Pie */}
          {(analytics.status_breakdown || []).length > 0 && (
            <Card className="bg-card/40 backdrop-blur border-border/50">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-gold" /> Status Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={analytics.status_breakdown} dataKey="count" nameKey="name"
                      cx="50%" cy="50%" outerRadius={65} innerRadius={40} paddingAngle={2}>
                      {analytics.status_breakdown.map((entry, i) => (
                        <Cell key={i} fill={STATUS_COLORS[entry.name] || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Platform Bar */}
          {(analytics.platform_breakdown || []).length > 0 && (
            <Card className="bg-card/40 backdrop-blur border-border/50">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5 text-gold" /> Platform Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={analytics.platform_breakdown}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.35} />
                    <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Posts" radius={[4, 4, 0, 0]}>
                      {analytics.platform_breakdown.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="rounded-lg bg-secondary/30 p-6 text-center">
          <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No posts assigned to this group yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Create posts and assign them to this group to see analytics.</p>
        </div>
      )}

      {/* Profile Performance */}
      {(analytics.profile_stats || []).length > 0 && (
        <Card className="bg-card/40 backdrop-blur border-border/50">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-xs font-semibold">Profile Performance</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="space-y-1.5">
              {analytics.profile_stats.map((prof, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-md bg-secondary/30">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`text-[10px] border capitalize ${PLATFORM_COLORS[prof.platform] || 'bg-secondary'}`}>
                      {prof.platform}
                    </Badge>
                    <span className="text-xs font-medium truncate">{prof.name}</span>
                    {prof.location && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <MapPin className="h-2.5 w-2.5" /> {prof.location}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium tabular-nums">{prof.post_count} posts</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Main Page ───
export default function ClientGroupsPage() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddProfile, setShowAddProfile] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const emptyForm = { name: '', description: '', assigned_members: [], client_email: '', client_phone: '', client_contact_name: '', client_business_name: '', client_address: '', client_notes: '' };
  const [form, setForm] = useState(emptyForm);
  const [profileForm, setProfileForm] = useState({ name: '', platform: 'facebook', account_id: '', location: '' });
  const [analyticsGroupId, setAnalyticsGroupId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  const fetchGroups = useCallback(() => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    clientGroupAPI.list(currentWorkspace.id)
      .then(res => setGroups(res.data))
      .catch(() => toast.error('Failed to load client groups'))
      .finally(() => setLoading(false));
  }, [currentWorkspace?.id]);

  const fetchMembers = useCallback(() => {
    if (!currentWorkspace?.id) return;
    workspaceAPI.listMembers(currentWorkspace.id)
      .then(res => setTeamMembers(res.data))
      .catch(() => {});
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchGroups(); fetchMembers(); }, [fetchGroups, fetchMembers]);

  const toggleMember = (userId) => {
    setForm(f => {
      const current = f.assigned_members || [];
      if (current.includes(userId)) {
        return { ...f, assigned_members: current.filter(id => id !== userId) };
      }
      return { ...f, assigned_members: [...current, userId] };
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Group name is required');
    try {
      await clientGroupAPI.create({ workspace_id: currentWorkspace.id, ...form });
      toast.success('Client group created');
      setShowCreate(false);
      setForm(emptyForm);
      fetchGroups();
    } catch { toast.error('Failed to create group'); }
  };

  const handleUpdate = async () => {
    if (!showEdit?.id) return;
    try {
      await clientGroupAPI.update(currentWorkspace.id, showEdit.id, form);
      toast.success('Group updated');
      setShowEdit(null);
      setForm(emptyForm);
      fetchGroups();
    } catch { toast.error('Failed to update group'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await clientGroupAPI.delete(currentWorkspace.id, deleteTarget.id);
      toast.success('Group deleted');
      setDeleteTarget(null);
      if (analyticsGroupId === deleteTarget.id) setAnalyticsGroupId(null);
      fetchGroups();
    } catch { toast.error('Failed to delete group'); }
  };

  const handleAddProfile = async () => {
    if (!profileForm.name.trim()) return toast.error('Profile name is required');
    try {
      await clientGroupAPI.addProfile(currentWorkspace.id, showAddProfile.id, profileForm);
      toast.success('Profile added');
      setShowAddProfile(null);
      setProfileForm({ name: '', platform: 'facebook', account_id: '', location: '' });
      fetchGroups();
    } catch { toast.error('Failed to add profile'); }
  };

  const handleRemoveProfile = async (groupId, profileId) => {
    try {
      await clientGroupAPI.removeProfile(currentWorkspace.id, groupId, profileId);
      toast.success('Profile removed');
      fetchGroups();
    } catch { toast.error('Failed to remove profile'); }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-secondary rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-40 bg-secondary rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in-up" data-testid="client-groups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Client Groups</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage multi-location clients and franchise brands under <span className="text-gold font-medium">{currentWorkspace?.name}</span>
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setShowCreate(true); }} data-testid="create-group-button">
          <Plus className="h-4 w-4 mr-2" /> New Group
        </Button>
      </div>

      {/* Groups */}
      {groups.length === 0 ? (
        <Card className="bg-card/60 backdrop-blur">
          <CardContent className="py-16 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-display font-medium mb-1">No Client Groups Yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              Create groups to organize multiple social profiles under a single client — perfect for franchises or businesses with multiple locations.
            </p>
            <Button onClick={() => { setForm(emptyForm); setShowCreate(true); }} data-testid="empty-create-group-button">
              <Plus className="h-4 w-4 mr-2" /> Create Your First Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map(group => {
            const isExpanded = analyticsGroupId === group.id;
            return (
              <Card
                key={group.id}
                className={`bg-card/60 backdrop-blur transition-all ${isExpanded ? 'border-primary/40 ring-1 ring-primary/20' : 'hover:border-primary/20'}`}
                data-testid={`group-card-${group.id}`}
              >
                <CardContent className="p-4">
                  {/* Group Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-display font-semibold truncate">{group.name}</h3>
                        {group.description && <p className="text-xs text-muted-foreground truncate">{group.description}</p>}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {(group.profiles || []).length} profiles</span>
                          <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {group.post_count || 0} posts</span>
                          {group.client_contact_name && <span className="text-primary">{group.client_contact_name}</span>}
                          {group.client_email && <span>{group.client_email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        title="Content Calendar"
                        onClick={() => navigate(`/clients/${group.id}/content-calendar`)}
                        data-testid={`group-calendar-${group.id}`}
                      >
                        <CalendarDays className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        title="Social Timeline"
                        onClick={() => navigate(`/clients/${group.id}/social-timeline`)}
                        data-testid={`group-timeline-${group.id}`}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant={isExpanded ? 'secondary' : 'outline'}
                        size="sm"
                        className="h-8 text-xs gap-1.5"
                        onClick={() => setAnalyticsGroupId(isExpanded ? null : group.id)}
                        data-testid={`toggle-analytics-${group.id}`}
                      >
                        <BarChart3 className="h-3.5 w-3.5" />
                        {isExpanded ? 'Hide' : 'Analytics'}
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => { setForm({ name: group.name, description: group.description || '', assigned_members: group.assigned_members || [], client_email: group.client_email || '', client_phone: group.client_phone || '', client_contact_name: group.client_contact_name || '', client_business_name: group.client_business_name || '', client_address: group.client_address || '', client_notes: group.client_notes || '' }); setShowEdit(group); }}
                        data-testid={`edit-group-${group.id}`}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(group)}
                        data-testid={`delete-group-${group.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Profiles Row */}
                  {(group.profiles || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(group.profiles || []).map(profile => (
                        <div key={profile.id} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/30 group/profile">
                          <Badge className={`text-[10px] border capitalize ${PLATFORM_COLORS[profile.platform] || 'bg-secondary'}`}>
                            {profile.platform}
                          </Badge>
                          <span className="text-xs font-medium">{profile.name}</span>
                          {profile.location && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <MapPin className="h-2.5 w-2.5" /> {profile.location}
                            </span>
                          )}
                          <button
                            className="ml-0.5 opacity-0 group-hover/profile:opacity-100 transition-opacity"
                            onClick={() => handleRemoveProfile(group.id, profile.id)}
                            data-testid={`remove-profile-${profile.id}`}
                          >
                            <X className="h-3 w-3 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Assigned Team Members */}
                  {(group.assigned_member_details || []).length > 0 && (
                    <div className="flex items-center gap-2 mt-3" data-testid={`group-members-${group.id}`}>
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      <div className="flex flex-wrap gap-1.5">
                        {group.assigned_member_details.map(m => (
                          <span key={m.id} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/15">
                            {m.name || m.email}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Profile */}
                  <Button
                    variant="outline" size="sm" className="mt-3 h-7 text-xs"
                    onClick={() => {
                      setProfileForm({ name: '', platform: 'facebook', account_id: '', location: '' });
                      setShowAddProfile(group);
                    }}
                    data-testid={`add-profile-to-${group.id}`}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Profile
                  </Button>

                  {/* Analytics Dashboard */}
                  {isExpanded && (
                    <GroupAnalyticsPanel workspaceId={currentWorkspace?.id} group={group} />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Group Dialog */}
      <Dialog open={showCreate || !!showEdit} onOpenChange={open => { if (!open) { setShowCreate(false); setShowEdit(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEdit ? 'Edit Client Group' : 'Create Client Group'}</DialogTitle>
            <DialogDescription>
              {showEdit ? 'Update group details and team assignments.' : 'Group multiple social profiles under one client and assign team members.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                placeholder="e.g. Acme Corp - Franchise"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="group-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Optional description..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                data-testid="group-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <UserPlus className="h-3.5 w-3.5" /> Assign Team Members
              </Label>
              <p className="text-xs text-muted-foreground">Select who is responsible for this client's content.</p>
              {teamMembers.length === 0 ? (
                <p className="text-xs text-muted-foreground/60 py-2">No team members found. Invite members first.</p>
              ) : (
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto rounded-lg border p-2" data-testid="member-assignment-list">
                  {teamMembers.map(m => {
                    const userId = m.user_id || m.user?.id;
                    const name = m.user?.name || m.user?.email || 'Unknown';
                    const role = m.role || m.user?.role || '';
                    const isSelected = (form.assigned_members || []).includes(userId);
                    return (
                      <label
                        key={userId}
                        className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 border border-primary/20' : 'hover:bg-secondary/40'}`}
                        data-testid={`assign-member-${userId}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleMember(userId)}
                        />
                        <div className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-semibold">{(name).charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium truncate">{name}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{role}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Client Contact Information */}
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs font-semibold tracking-wide text-foreground">Client Contact Information</Label>
              <p className="text-xs text-muted-foreground">Enter the client's details. They'll receive approval notifications at this email.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px]">Contact Name</Label>
                  <Input placeholder="e.g. John Smith" value={form.client_contact_name} onChange={e => setForm(f => ({ ...f, client_contact_name: e.target.value }))} data-testid="client-contact-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Business Name</Label>
                  <Input placeholder="e.g. Acme Corp" value={form.client_business_name} onChange={e => setForm(f => ({ ...f, client_business_name: e.target.value }))} data-testid="client-business-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px]">Client Email</Label>
                  <Input type="email" placeholder="client@company.com" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} data-testid="client-email-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Phone</Label>
                  <Input placeholder="+1 (555) 000-0000" value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} data-testid="client-phone-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Address</Label>
                <Input placeholder="123 Main St, City, State" value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))} data-testid="client-address-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Notes</Label>
                <Input placeholder="Any special requirements or notes about this client..." value={form.client_notes} onChange={e => setForm(f => ({ ...f, client_notes: e.target.value }))} data-testid="client-notes-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setShowEdit(null); }}>Cancel</Button>
            <Button onClick={showEdit ? handleUpdate : handleCreate} data-testid="save-group-button">
              {showEdit ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Profile Dialog */}
      <Dialog open={!!showAddProfile} onOpenChange={open => { if (!open) setShowAddProfile(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Profile to {showAddProfile?.name}</DialogTitle>
            <DialogDescription>Add a social media profile or location to this group.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Profile Name</Label>
              <Input
                placeholder="e.g. Acme Downtown"
                value={profileForm.name}
                onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={profileForm.platform} onValueChange={v => setProfileForm(f => ({ ...f, platform: v }))}>
                <SelectTrigger data-testid="profile-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Location (optional)</Label>
              <Input
                placeholder="e.g. New York, NY"
                value={profileForm.location}
                onChange={e => setProfileForm(f => ({ ...f, location: e.target.value }))}
                data-testid="profile-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Account ID (optional)</Label>
              <Input
                placeholder="Platform account ID"
                value={profileForm.account_id}
                onChange={e => setProfileForm(f => ({ ...f, account_id: e.target.value }))}
                data-testid="profile-account-id-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProfile(null)}>Cancel</Button>
            <Button onClick={handleAddProfile} data-testid="save-profile-button">Add Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the group and unlink all associated posts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-group">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
